import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { darkColors, lightColors, typography } from '../theme';
import { useTheme, useLanguage } from '../contexts/AppContext';
import { useWalkthrough } from '../contexts/WalkthroughContext';

export function WalkthroughOverlay() {
  const { isDark } = useTheme();
  const { t } = useLanguage();
  const { isOpen, stepIndex, steps, next, prev, close } = useWalkthrough();
  const colors = isDark ? darkColors : lightColors;
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);

  const step = steps[stepIndex];
  const stepLabel = (step?.label && step.label.startsWith('walkthrough.')) ? t(step.label) : (step?.label ?? '');
  const stepDescription = (step?.description && step.description.startsWith('walkthrough.')) ? t(step.description) : (step?.description ?? '');

  useEffect(() => {
    if (!isOpen || !step) {
      setSpotlightRect(null);
      return;
    }
    const el = document.getElementById(step.targetId);
    if (!el) {
      setSpotlightRect(null);
      return;
    }
    const update = () => setSpotlightRect(el.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('scroll', update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', update, true);
    };
  }, [isOpen, step, stepIndex]);

  if (!isOpen) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const padding = 6;

  return (
    <View
      style={{
        position: 'fixed' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 100000,
        pointerEvents: 'auto',
      }}
    >
      {/* Blur overlay with cutout: four panels so the spotlight area stays clear */}
      {spotlightRect ? (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: Math.max(0, spotlightRect.top - padding),
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              pointerEvents: 'auto',
            }}
            onClick={close}
          />
          <div
            style={{
              position: 'fixed',
              top: spotlightRect.top - padding,
              left: 0,
              width: Math.max(0, spotlightRect.left - padding),
              height: spotlightRect.height + padding * 2,
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              pointerEvents: 'auto',
            }}
            onClick={close}
          />
          <div
            style={{
              position: 'fixed',
              top: spotlightRect.top - padding,
              left: spotlightRect.right + padding,
              right: 0,
              height: spotlightRect.height + padding * 2,
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              pointerEvents: 'auto',
            }}
            onClick={close}
          />
          <div
            style={{
              position: 'fixed',
              top: spotlightRect.bottom + padding,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.55)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              pointerEvents: 'auto',
            }}
            onClick={close}
          />
        </>
      ) : (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
          onClick={close}
        />
      )}

      {/* Spotlight ring around sidebar item */}
      {spotlightRect && (
        <div
          style={{
            position: 'fixed',
            top: spotlightRect.top - 4,
            left: spotlightRect.left - 4,
            width: spotlightRect.width + 8,
            height: spotlightRect.height + 8,
            borderRadius: 10,
            border: `3px solid ${colors.accent}`,
            boxShadow: `0 0 20px ${colors.accent}50`,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Card with description and buttons */}
      <div
        style={{
          position: 'fixed',
          bottom: 32,
          left: '50%',
          transform: 'translateX(-50%)',
          minWidth: 320,
          maxWidth: 420,
          padding: 20,
          backgroundColor: colors.surface,
          borderRadius: parseInt(colors.borderRadius.large),
          borderWidth: 1,
          borderColor: colors.border,
          borderStyle: 'solid',
          boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
          pointerEvents: 'auto',
        }}
      >
        <Text
          style={{
            color: colors.accent,
            fontSize: parseInt(typography.fontSize.small),
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: 1.2,
            marginBottom: 6,
            fontFamily: typography.fontFamily.body,
          }}
        >
          {stepLabel}
        </Text>
        <Text
          style={{
            color: colors.textPrimary,
            fontSize: parseInt(typography.fontSize.default),
            lineHeight: 22,
            marginBottom: 20,
            fontFamily: typography.fontFamily.body,
          }}
        >
          {stepDescription}
        </Text>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity
              onPress={close}
              style={{
                paddingVertical: 10,
                paddingHorizontal: 16,
                borderRadius: parseInt(colors.borderRadius.medium),
                backgroundColor: colors.surfaceCard,
                borderWidth: 1,
                borderColor: colors.border,
                cursor: 'pointer',
              }}
            >
              <Text style={{ color: colors.textSecondary, fontWeight: '600', fontSize: 14 }}>{t('walkthrough.skip')}</Text>
            </TouchableOpacity>
            {!isFirst && (
              <TouchableOpacity
                onPress={prev}
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  borderRadius: parseInt(colors.borderRadius.medium),
                  backgroundColor: colors.surfaceCard,
                  borderWidth: 1,
                  borderColor: colors.border,
                  cursor: 'pointer',
                }}
              >
                <Text style={{ color: colors.textPrimary, fontWeight: '600', fontSize: 14 }}>{t('walkthrough.back')}</Text>
              </TouchableOpacity>
            )}
          </View>
          {isLast ? (
            <TouchableOpacity
              onPress={close}
              style={{
                paddingVertical: 10,
                paddingHorizontal: 20,
                borderRadius: parseInt(colors.borderRadius.medium),
                backgroundColor: colors.accent,
                cursor: 'pointer',
              }}
            >
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>{t('walkthrough.done')}</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              onPress={next}
              style={{
                paddingVertical: 10,
                paddingHorizontal: 20,
                borderRadius: parseInt(colors.borderRadius.medium),
                backgroundColor: colors.accent,
                cursor: 'pointer',
              }}
            >
              <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>{t('walkthrough.next')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </div>
    </View>
  );
}
